import { cva } from '@/styled-system/css';

/*
 * Trigger cosmetics are authored once and applied at two cascade strengths:
 * - Normal specificity for the built-in IconButton, which resolves deterministically via
 *   IconButton's own `css(recipe.raw(), cssProp)` object merge.
 * - Zero specificity (`_slotted`, Panda's custom condition for `:where(&)`) for a Radix
 *   `Slot`-merged custom child, whose own recipe atoms and consumer `css` overrides share the
 *   DOM node and must win the cascade.
 * Do not derive these at runtime (e.g. `css({ _slotted: recipe.raw() })`) — Panda's static
 * extractor can't see that, so the classes end up on the node but missing from the stylesheet.
 */
const sizeSm = {
  paddingInline: 'tight',
  borderTopRightRadius: 'obj.default',
  borderBottomRightRadius: 'obj.default',
  borderTopLeftRadius: 'none',
  borderBottomLeftRadius: 'none',
};

const sizeMd = {
  paddingInline: 'tight',
  borderTopRightRadius: 'obj.default',
  borderBottomRightRadius: 'obj.default',
  borderTopLeftRadius: 'none',
  borderBottomLeftRadius: 'none',
};

const sizeLg = {
  paddingInline: 'default',
  borderTopRightRadius: 'obj.default',
  borderBottomRightRadius: 'obj.default',
  borderTopLeftRadius: 'none',
  borderBottomLeftRadius: 'none',
};

const focusRing = {
  transitionProperty: {
    _motionReduce: 'none',
    _motionSafe: 'box-shadow',
  },
  transitionDuration: {
    _motionReduce: '0s',
    _motionSafe: '{durations.200}',
  },
  transitionTimingFunction: '{easings.ease}',
  boxShadow: {
    _focusVisible: 'inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}',
  },
};

const focusRingHeroColor = {
  boxShadowColor: {
    _focusVisible: 'border.action.hero.default',
  },
};

const focusRingCriticalColor = {
  boxShadowColor: {
    _focusVisible: 'border.action.critical.strong.default',
  },
};

/**
 * Normal-specificity trigger styles for the built-in `IconButton` trigger.
 */
export const datePickerTriggerRecipe = cva({
  base: {},
  variants: {
    size: {
      sm: sizeSm,
      md: sizeMd,
      lg: sizeLg,
    },
    disabled: {
      true: {},
      false: focusRing,
    },
    error: {
      true: {},
      false: {},
    },
    readOnly: {
      true: {
        display: 'none',
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      disabled: false,
      error: false,
      css: focusRingHeroColor,
    },
    {
      disabled: false,
      error: true,
      css: focusRingCriticalColor,
    },
  ],
});

/**
 * Zero-specificity trigger styles for a custom child merged onto the trigger via `Slot`.
 * Cosmetics are wrapped in the `_slotted` condition (`:where(&)`) so the child's own recipe and
 * any consumer `css` win the cascade; `readOnly`'s `display: none` stays at normal specificity
 * since it's availability, not a cosmetic default a styled child should be able to override.
 */
export const datePickerTriggerSlottedRecipe = cva({
  base: {},
  variants: {
    size: {
      sm: { _slotted: sizeSm },
      md: { _slotted: sizeMd },
      lg: { _slotted: sizeLg },
    },
    disabled: {
      true: {},
      false: { _slotted: focusRing },
    },
    error: {
      true: {},
      false: {},
    },
    readOnly: {
      true: {
        display: 'none',
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      disabled: false,
      error: false,
      css: { _slotted: focusRingHeroColor },
    },
    {
      disabled: false,
      error: true,
      css: { _slotted: focusRingCriticalColor },
    },
  ],
});
